import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { sopService } from '@/services/sop.service';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

export function CategoriesDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const query = useQuery({
    queryKey: ['sop', 'categories'],
    queryFn: () => sopService.listCategories(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['sop', 'categories'] });

  const create = useMutation({
    mutationFn: (n: string) => sopService.createCategory({ name: n }),
    onSuccess: () => { toast.success('Category added'); setName(''); void invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => sopService.deleteCategory(id),
    onSuccess: () => {
      toast.success('Category deleted');
      void invalidate();
      void queryClient.invalidateQueries({ queryKey: ['sop', 'sops'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle>SOP Categories</DialogTitle>
        </DialogHeader>

        <form
          className="flex gap-2"
          onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate(name.trim()); }}
        >
          <Input placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" disabled={!name.trim() || create.isPending}>
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </form>

        <div className="space-y-1.5">
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (query.data ?? []).length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No categories yet</p>
          ) : (
            (query.data ?? []).map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2 text-sm">
                <span>{c.name}</span>
                <button type="button" onClick={() => remove.mutate(c.id)} className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label="Delete category">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
